import React from 'react';
import { Modal } from 'app-components';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import Dropdown from '../../../../lib/inputs/Dropdown';
import { useFormHandler } from '../hooks/useFormHandler';
import { ROOT_STATE } from '../../../../store/store.types';
import { handleChange } from '../constants/ticketlist.utils';
import { defaultOptions, taskStatusTypes, ticketStatusTypes } from '../constants/ticketlist.constants';
import GIButton from '../../../../components/common/button/GIButton';
import { INPUT_TYPES } from '../../../../constants/inputs/inputTypes';
import { useApiHandler } from '../hooks/useApiHandler';
import { IBulkReassignTicketModal } from '../constants/ticketlist.interface';
import { StyledFlexCenter, StyledModal, StyledError } from '../constants/ticketlist.styled';
import { API_STATUS_CODES } from '../../../../constants/common.constants';

export const BulkReassignTicketModal = ({ onClose, selectedRows }: IBulkReassignTicketModal) => {
    const dispatch = useDispatch();

    const {
        bulkAssignmentUsers,
        tickets: { ticketListJson },
    } = useSelector((state: ROOT_STATE) => state.ticketMgmt.ticketsList);

    const { bulkReassignTicketAPI } = useApiHandler();
    const { bulkReassignTicketForm } = useFormHandler();
    const {
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
    } = bulkReassignTicketForm;
    const values = watch();

    const onSubmit = async (formData: any) => {
        const { assignee } = formData;
        const payload = {
            assignee,
            dataType: 'json',
            ticketId: selectedRows.join(','),
        };
        const { statusCode } = await bulkReassignTicketAPI(dispatch, payload);
        if (statusCode === API_STATUS_CODES.OK) {
            const updatedTickets = [...ticketListJson];
            selectedRows.forEach(ticketId => {
                const index = ticketListJson.findIndex(ticket => ticket.id === ticketId);
                if (index !== -1) {
                    updatedTickets[index] = {
                        ...updatedTickets[index],
                        assignee: bulkAssignmentUsers[assignee],
                        taskStatus: taskStatusTypes.OPEN,
                        status: ticketStatusTypes.OPEN,
                    };
                }
            });
            dispatch({ type: 'UPDATE_TICKETS', payload: updatedTickets });
        }
        onClose();
    };

    return (
        <StyledModal>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Modal
                    id="bulkreassignTicketModal"
                    classNames="reassign-ticket-modal"
                    data-testid="bulk-reassign-modal"
                >
                    <Modal.Header
                        id="bulkreassignTicketModalHeader"
                        titleId="ticketList.modal.bulkreassignTicketModal.headerReassign"
                        data-testid="bulk-reassign-modal-header"
                        onHide={onClose}
                    />
                    <Modal.Body data-testid="bulk-reassign-modal-body">
                        <Dropdown
                            label={{
                                text: <FormattedMessage id="ticketList.modal.reassignDropdown.label" />,
                                labelClass: ['dropdown-label'],
                            }}
                            inputData={{
                                inputClass: ['dropdown-input'],
                                name: 'assignee',
                                type: INPUT_TYPES.DROPDOWN,
                                value: values.assignee,
                                isInputRequired: true,
                                options: [
                                    ...defaultOptions,
                                    ...Object.keys(bulkAssignmentUsers).map(key => ({
                                        value: key,
                                        text: bulkAssignmentUsers[key],
                                    })),
                                ],
                            }}
                            inputActions={{
                                handleFieldChange: value => handleChange('assignee', value, setValue),
                                onFieldBlur: () => {},
                            }}
                        />
                        <StyledFlexCenter>
                            <StyledError data-testid="bulk-assignee-error">{errors?.assignee?.message}</StyledError>
                        </StyledFlexCenter>
                    </Modal.Body>
                    <Modal.Controls data-testid="bulk-reassign-modal-controls">
                        <GIButton
                            id="bulkreassignTicketModalCancelButton"
                            onClickHandler={onClose}
                            variant="secondary"
                            labelId="button.cancel"
                        />
                        <GIButton
                            id="bulkreassignTicketModalSaveButton"
                            type="submit"
                            variant="primary"
                            labelId="button.submit"
                        />
                    </Modal.Controls>
                </Modal>
            </form>
        </StyledModal>
    );
};
